import { Activity } from '../activity.model';
import { ActivityRole } from './activity.interfaces';
import { ActivityService } from '../activity.service';
import { forkJoin, map, pipe, switchMap } from 'rxjs';
import { Pipe, PipeTransform } from '@angular/core';
import {
  getScheduleInterval,
  getTargetScheduleInterval,
} from '../../_lib/schedule';
import { isCourse } from '../../course/course.util';
import { isCamp } from '../../camp/lib/camp.util';
import { extractDateLabel } from '../../../util/date';

export function isActivity(resource: unknown): resource is Activity {
  return (
    resource instanceof Activity ||
    (typeof resource === 'object' &&
      resource !== null &&
      (resource as any).staff !== undefined &&
      (resource as any).role !== undefined)
  );
}

export function getActivityStart(activity: Activity) {
  return getTargetScheduleInterval(activity.target).start;
}

export function getActivityEnd(activity: Activity) {
  return getTargetScheduleInterval(activity.target).end;
}

export const activityRoleLabels = {
  [ActivityRole.Monitor]: $localize`Monitor`,
  [ActivityRole.HeadMonitor]: $localize`Head Monitor`,
  [ActivityRole.Teacher]: $localize`Teacher`,
  [ActivityRole.GamesMonitor]: $localize`Games Monitor`,
  [ActivityRole.RegionalManager]: $localize`Regional Manager`,
} as const satisfies { [key in ActivityRole]: string };

export const roleImportance = [
  ActivityRole.RegionalManager,
  ActivityRole.HeadMonitor,
  ActivityRole.Teacher,
  ActivityRole.GamesMonitor,
  ActivityRole.Monitor,
];

export const activityRoleOptions = Object.entries(activityRoleLabels).map(
  ([key, value]) => ({ title: value, value: key as ActivityRole }),
);

export function updateActivityGroup(
  service: ActivityService,
  {
    mainActivity,
    overlappingActivities,
  }: { mainActivity?: Activity; overlappingActivities: Activity[] },
) {
  if (!mainActivity) {
    return pipe();
  }
  return pipe(
    switchMap((newActivity: Activity) => {
      // If id is missing, it must be the newly created activity.
      let newMainId = mainActivity.id ?? newActivity.id;
      // Replace the new activity placeholder with the new activity
      overlappingActivities = overlappingActivities.map((a) =>
        a.id === undefined ? newActivity : a,
      );
      // Now, find all activities that need their main updated
      const toUpdate = overlappingActivities.filter(
        (a) => a.id === newMainId || a.mainActivity?.id !== newMainId,
      );

      return forkJoin(
        toUpdate.map((sideActivity) =>
          service.update(sideActivity.id, {
            mainActivity:
              sideActivity.id === newMainId ? null : { id: newActivity.id },
          }),
        ),
        // Map back to original activity to restore observable flow.
      ).pipe(map(() => newActivity));
    }),
  );
}

@Pipe({
  name: 'activityInvolvementLabel',
  standalone: true,
})
export class ActivityInvolvementLabelPipe implements PipeTransform {
  transform(activity: Activity): string {
    const { role, course } = activity;

    const roleLabel = activityRoleLabels[role];
    if (role === ActivityRole.Monitor && course?.theme?.name) {
      return roleLabel + `: ` + course.theme.name;
    }
    return roleLabel;
  }
}

@Pipe({
  name: 'activityEvent',
  standalone: true,
})
export class ActivityEventPipe implements PipeTransform {
  transform(activity: Activity): string {
    return activity.target.name;
  }
}

@Pipe({
  name: 'activityTargetType',
  standalone: true,
})
export class ActivityTargetTypePipe implements PipeTransform {
  transform(activity: Activity): string {
    return isCourse(activity.target) || isCamp(activity.target)
      ? $localize`Camp`
      : $localize`Workshop`;
  }
}

@Pipe({
  name: 'activityRole',
  standalone: true,
})
export class ActivityRolePipe implements PipeTransform {
  transform(activity: Activity): string {
    const role = activityRoleLabels[activity.role];
    return (
      role + (isCourse(activity.target) ? ' ' + activity.target.theme.name : '')
    );
  }
}

@Pipe({
  name: 'activityPeriod',
  standalone: true,
})
export class ActivityPeriodPipe implements PipeTransform {
  transform(activity: Activity): string {
    const rootTarget = activity.rootTarget;
    const period =
      isCamp(rootTarget) && rootTarget.period
        ? `${rootTarget.period.label} ${rootTarget.period.week}`
        : null;
    return (
      period ?? extractDateLabel(getScheduleInterval(rootTarget.schedule).start)
    );
  }
}

@Pipe({
  name: 'activityInterval',
  standalone: true,
})
export class ActivityIntervalPipe implements PipeTransform {
  transform(activity: Activity): string {
    const rootTarget = activity.rootTarget;
    const { start, end } = getScheduleInterval(rootTarget.schedule);
    return extractDateLabel(start) + ' - ' + extractDateLabel(end);
  }
}

@Pipe({
  name: 'activityStart',
  standalone: true,
})
export class ActivityStartPipe implements PipeTransform {
  transform(activity: Activity): string {
    const rootTarget = activity.rootTarget;
    return extractDateLabel(getScheduleInterval(rootTarget.schedule).start);
  }
}
